package com.swetdot.swetgram.protocol.dto

import com.swetdot.swetgram.protocol.ResourceVersion
import com.swetdot.swetgram.protocol.Timestamp
import com.swetdot.swetgram.protocol.Uuid
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.descriptors.buildClassSerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder
import kotlinx.serialization.json.JsonDecoder
import kotlinx.serialization.json.JsonEncoder
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** `PageInfo`. Cursor pagination; `has_more` is explicit rather than inferred from page size. */
@Serializable
data class PageInfo(
    @SerialName("next_cursor") val nextCursor: String? = null,
    @SerialName("has_more") val hasMore: Boolean,
    @SerialName("newer_cursor") val newerCursor: String? = null,
    @SerialName("has_newer") val hasNewer: Boolean = false,
)

@Serializable
enum class MessageType {
    @SerialName("text") TEXT,
    @SerialName("image") IMAGE,
    @SerialName("voice") VOICE,
    @SerialName("system") SYSTEM,
    @SerialName("discover_post") DISCOVER_POST,
    @SerialName("picso_share") PICSO_SHARE,
    @SerialName("unknown") UNKNOWN,
}

/**
 * Server-visible message status. The contract defines ONLY these two -- `sending` and `failed`
 * are client-local and never appear on the wire, and there is deliberately no `delivered`
 * state in the MVP (DECISIONS.md §5).
 */
@Serializable
enum class MessageStatus {
    @SerialName("sent") SENT,
    @SerialName("read") READ,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
enum class MediaPurpose {
    @SerialName("avatar") AVATAR,
    @SerialName("chat_image") CHAT_IMAGE,
    @SerialName("chat_voice") CHAT_VOICE,
    @SerialName("group_avatar") GROUP_AVATAR,
    @SerialName("picso_photo") PICSO_PHOTO,
    @SerialName("unknown") UNKNOWN,
}

@Serializable
data class MediaObject(
    @SerialName("id") val id: Uuid,
    @SerialName("purpose") val purpose: MediaPurpose = MediaPurpose.UNKNOWN,
    @SerialName("mime_type") val mimeType: String,
    @SerialName("size_bytes") val sizeBytes: Long,
    @SerialName("width") val width: Int,
    @SerialName("height") val height: Int,
    /** Present only for `chat_voice`; verified from the uploaded Ogg/Opus stream by the server. */
    @SerialName("duration_ms") val durationMs: Long? = null,
    /** 0..100 amplitude samples used to draw the voice bubble without downloading the audio. */
    @SerialName("waveform") val waveform: List<Int> = emptyList(),
    @SerialName("codec") val codec: String? = null,
    /** Public CDN URL for ordinary media; Picso uses an authenticated API URI-reference. */
    @SerialName("url") val url: String,
)

@Serializable
data class MessageReference(
    @SerialName("id") val id: Uuid,
    @SerialName("sender_id") val senderId: Uuid,
    @SerialName("sender_display_name") val senderDisplayName: String? = null,
    @SerialName("type") val type: MessageType = MessageType.UNKNOWN,
    @SerialName("preview") val preview: String? = null,
)

@Serializable
data class MessageReaction(
    @SerialName("emoji") val emoji: String,
    @SerialName("count") val count: Int,
    @SerialName("reacted_by_me") val reactedByMe: Boolean = false,
)

@Serializable
data class ReactToMessageRequest(
    @SerialName("emoji") val emoji: String,
)

@Serializable
data class EditMessageRequest(
    @SerialName("content") val content: String,
    @SerialName("version") val version: ResourceVersion,
)

/**
 * `DiscoverPostReference` -- the `shared_post` on a `Message` of type `discover_post`, produced by
 * `shareDiscoverPostToConversation`.
 *
 * THIS TYPE WAS WRONG UNTIL NOW AND THE FAILURE WAS SILENT, which is why it is written down. The
 * frozen contract requires FOUR properties -- `id`, `author_display_name`, `content_preview`
 * (maxLength 240) and `url` (format uri). The declaration here carried
 * `{id, author: UserPublic?, preview: String?}`: it INVENTED `author` and `preview` and omitted all
 * three of the others. A conforming server response decoded with both invented fields null, because
 * both were nullable with defaults, so a shared post rendered as a bare id with no author and no
 * text and nothing anywhere reported an error.
 *
 * NO PROPERTY HAS A KOTLIN DEFAULT, deliberately and for the same reason `Discover.kt` gives: all
 * four are contract-REQUIRED, `coerceInputValues` substitutes a DECLARED default, and a property
 * without one fails the decode instead of quietly becoming something the server never sent. That is
 * what turns the next mismatch into a visible failure rather than an empty card.
 *
 * [authorDisplayName] and [contentPreview] are a SNAPSHOT taken when the post was shared and are
 * never re-derived -- `messages.shared_post_author_display_name` and
 * `messages.shared_post_content_preview` (V9), which `messages_shared_post_complete` keeps together
 * with the id. The cost is stated in V9: an edited post's shared preview goes stale. The
 * alternative is a live join that would evaluate Discover's read predicate on behalf of a
 * conversation whose members are not the group's, and leak a private group's post on every render.
 *
 * [url] is DERIVED from the base URL and [id], never stored, exactly as `Group.public_url` and
 * `UserPublic.profile_url` are.
 *
 * `shareDiscoverPostToConversation` constructs this snapshot and stores its three source fields on
 * the message row. Message reads reconstruct it without re-reading the original Discover post.
 */
@Serializable
data class DiscoverPostReference(
    @SerialName("id") val id: Uuid,
    @SerialName("author_display_name") val authorDisplayName: String,
    /** `maxLength: 240`. Truncation belongs to the writer; `messages_shared_post_preview_len` is
     *  the floor under it, so a preview that was never truncated cannot reach a client that trusts
     *  the contract's bound. */
    @SerialName("content_preview") val contentPreview: String,
    /** `format: uri`. */
    @SerialName("url") val url: String,
)

/**
 * `MessageSender` is an UNTAGGED `oneOf: [UserPublic, DeletedUser]` -- the two variants are
 * distinguished only by the presence of `is_deleted: true`, with no discriminator property.
 * kotlinx.serialization cannot express that with sealed-class polymorphism, which always
 * writes a discriminator, so this needs a hand-written serializer that emits the variant's
 * fields FLAT. Nesting them under a wrapper key would not match the contract.
 */
@Serializable(with = MessageSenderSerializer::class)
sealed interface MessageSender {
    data class Known(val user: UserPublic) : MessageSender
    data class Deleted(val user: DeletedUser) : MessageSender

    val displayName: String
        get() = when (this) {
            is Known -> user.displayName
            is Deleted -> user.displayName
        }

    val id: Uuid?
        get() = when (this) {
            is Known -> user.id
            is Deleted -> user.id
        }
}

object MessageSenderSerializer : KSerializer<MessageSender> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("MessageSender")

    override fun deserialize(decoder: Decoder): MessageSender {
        val input = decoder as? JsonDecoder
            ?: error("MessageSender can only be read from JSON")
        val obj = input.decodeJsonElement().jsonObject
        val isDeleted = obj["is_deleted"]?.jsonPrimitive?.booleanOrNull == true
        return if (isDeleted) {
            MessageSender.Deleted(input.json.decodeFromJsonElement(DeletedUser.serializer(), obj))
        } else {
            MessageSender.Known(input.json.decodeFromJsonElement(UserPublic.serializer(), obj))
        }
    }

    override fun serialize(encoder: Encoder, value: MessageSender) {
        val output = encoder as? JsonEncoder
            ?: error("MessageSender can only be written as JSON")
        val element = when (value) {
            is MessageSender.Known ->
                output.json.encodeToJsonElement(UserPublic.serializer(), value.user)
            is MessageSender.Deleted ->
                output.json.encodeToJsonElement(DeletedUser.serializer(), value.user)
        }
        output.encodeJsonElement(element)
    }
}

@Serializable
data class Message(
    @SerialName("id") val id: Uuid,
    /** Client-generated idempotency key, echoed back so an optimistic row can be reconciled. */
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("conversation_id") val conversationId: Uuid,
    @SerialName("sender") val sender: MessageSender,
    @SerialName("type") val type: MessageType = MessageType.UNKNOWN,
    @SerialName("content") val content: String? = null,
    @SerialName("media") val media: MediaObject? = null,
    @SerialName("reply_to_message") val replyToMessage: MessageReference? = null,
    @SerialName("status") val status: MessageStatus = MessageStatus.UNKNOWN,
    @SerialName("created_at") val createdAt: Timestamp,
    /** Diagnostic only. Server time is authoritative; never order by this. */
    @SerialName("client_created_at") val clientCreatedAt: Timestamp? = null,
    @SerialName("version") val version: ResourceVersion,
    @SerialName("edited_at") val editedAt: Timestamp? = null,
    @SerialName("can_edit") val canEdit: Boolean = false,
    @SerialName("can_delete_for_everyone") val canDeleteForEveryone: Boolean = false,
    @SerialName("mentions") val mentions: List<Uuid> = emptyList(),
    @SerialName("reactions") val reactions: List<MessageReaction> = emptyList(),
    @SerialName("shared_post") val sharedPost: DiscoverPostReference? = null,
    @SerialName("picso") val picso: PicsoReference? = null,
)

@Serializable
data class SendMessageRequest(
    /** Required. Paired with the `Idempotency-Key` header for safe retries. */
    @SerialName("client_id") val clientId: Uuid,
    @SerialName("type") val type: MessageType,
    @SerialName("content") val content: String? = null,
    @SerialName("media_id") val mediaId: Uuid? = null,
    @SerialName("reply_to_message_id") val replyToMessageId: Uuid? = null,
    @SerialName("client_created_at") val clientCreatedAt: Timestamp? = null,
)

/** Creates a new message in another conversation from content the caller can currently read. */
@Serializable
data class ForwardMessageRequest(
    @SerialName("conversation_id") val conversationId: Uuid,
    /** Client id plus Idempotency-Key make retries safe and preserve the selected order. */
    @SerialName("client_id") val clientId: Uuid,
)

@Serializable
data class MessagePage(
    @SerialName("items") val items: List<Message> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
)

/**
 * Tombstones survive 30 days so a client that was offline learns about deletions it never saw.
 * A last-message cursor alone cannot report the deletion of an OLDER message, which is exactly
 * why `change_cursor` exists (IMPLEMENTATION_NOTES.md §5).
 */
@Serializable
data class MessageTombstone(
    @SerialName("id") val id: Uuid,
    @SerialName("conversation_id") val conversationId: Uuid,
    @SerialName("deleted_at") val deletedAt: Timestamp,
    @SerialName("expires_at") val expiresAt: Timestamp,
    @SerialName("deleted") val deleted: Boolean = true,
)

/** Another untagged union: a full [Message], or a [MessageTombstone] carrying `deleted: true`. */
@Serializable(with = MessageChangeSerializer::class)
sealed interface MessageChange {
    data class Updated(val message: Message) : MessageChange
    data class Deleted(val tombstone: MessageTombstone) : MessageChange
}

object MessageChangeSerializer : KSerializer<MessageChange> {
    override val descriptor: SerialDescriptor = buildClassSerialDescriptor("MessageChange")

    override fun deserialize(decoder: Decoder): MessageChange {
        val input = decoder as? JsonDecoder
            ?: error("MessageChange can only be read from JSON")
        val obj = input.decodeJsonElement().jsonObject
        // `deleted` is declared `const: true` but is NOT in MessageTombstone's `required` list,
        // so a conformant peer may omit it entirely -- discriminating on it alone would parse a
        // tombstone as a Message and then fail on the missing `sender`/`version`.
        // `sender` IS required on Message and absent from MessageTombstone, so its absence is
        // the reliable signal. `deleted` and `expires_at` are accepted as corroborating hints.
        val isTombstone = obj["deleted"]?.jsonPrimitive?.booleanOrNull == true ||
            (obj["sender"] == null && obj["expires_at"] != null)
        return if (isTombstone) {
            MessageChange.Deleted(input.json.decodeFromJsonElement(MessageTombstone.serializer(), obj))
        } else {
            MessageChange.Updated(input.json.decodeFromJsonElement(Message.serializer(), obj))
        }
    }

    override fun serialize(encoder: Encoder, value: MessageChange) {
        val output = encoder as? JsonEncoder
            ?: error("MessageChange can only be written as JSON")
        val element = when (value) {
            is MessageChange.Updated ->
                output.json.encodeToJsonElement(Message.serializer(), value.message)
            is MessageChange.Deleted ->
                output.json.encodeToJsonElement(MessageTombstone.serializer(), value.tombstone)
        }
        output.encodeJsonElement(element)
    }
}

@Serializable
data class MessageChangePage(
    @SerialName("items") val items: List<MessageChange> = emptyList(),
    @SerialName("page_info") val pageInfo: PageInfo,
    /** Persist this and send it back; it is what makes edits and tombstones reachable. */
    @SerialName("change_cursor") val changeCursor: String,
)
